import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { inputs } from '@ecosystem/newron-design-system';
import { Market, TimeFrame, TimeFrameOption } from '../types';
import { dateToTimeFrame, timeFrameToDate } from './monthsSelector.utils';
import { isCustomMonthly } from './periodSelector.utils';
import { ApplyPeriodButton } from './applyPeriod.button';
import { Periodicity } from '@ecosystem/newron-core/types';
import { WARNING_MESSAGE_ON_APPLY } from './constants';
import { useMobile } from '../../providers/mobile/mobile.provider';

const { MonthPicker, MonthFormatType } = inputs;

type TimeFrameOptionWithDate = {
  timeFrame: TimeFrameOption;
  date: Date;
};

export type Props = {
  monthlyOptions: TimeFrameOption[];
  markets: Market[];
  currentValue: TimeFrame;
  onSelect: (value: TimeFrameOption, removeAffectedMarkets: boolean) => void;
};

export function MonthsSelector({ monthlyOptions = [], markets = [], currentValue, onSelect }: Props) {
  const { isMobile } = useMobile();

  const [monthFrom, setMonthFrom] = useState<TimeFrameOptionWithDate>(null);
  const [monthTo, setMonthTo] = useState<TimeFrameOptionWithDate>(null);
  const [isWrongDateRange, setIsWrongDateRange] = useState(false);

  const notPublishedMarkets = markets.filter(m =>
    m.periods.find(p => p.periodicity === Periodicity.Monthly && p.sequence < monthTo?.timeFrame.endSequence)
  );

  useEffect(() => {
    setIsWrongDateRange(monthFrom?.date.getTime() > monthTo?.date.getTime());
  }, [monthFrom?.date, monthTo?.date]);

  const sortMonthlyOptions = useMemo(() => {
    return monthlyOptions.sort((a, b) => a.endSequence - b.endSequence);
  }, [monthlyOptions]);

  const sequenceToTimeFrame = useCallback(
    (month: number): TimeFrameOptionWithDate => {
      const timeFrame = sortMonthlyOptions.find(el => el.endSequence === month);
      if (!timeFrame) {
        return null;
      }

      return {
        date: timeFrameToDate(timeFrame),
        timeFrame,
      };
    },
    [sortMonthlyOptions]
  );

  useEffect(() => {
    const setCurrentlySelectedValueOnMonthPickers = () => {
      const fromMonth = sequenceToTimeFrame(currentValue.startSequence);
      const toMonth = sequenceToTimeFrame(currentValue.endSequence);
      if (fromMonth && toMonth) {
        setMonthFrom({
          timeFrame: fromMonth.timeFrame,
          date: fromMonth.date,
        });
        setMonthTo({
          timeFrame: toMonth.timeFrame,
          date: toMonth.date,
        });
      }
    };

    const setLastTwoMonthsOnMonthPickers = () => {
      if (sortMonthlyOptions.length < 2) {
        return;
      }
      // get the last month
      const lastMonthTimeFrame = sortMonthlyOptions[sortMonthlyOptions.length - 1];
      setMonthTo({
        timeFrame: lastMonthTimeFrame,
        date: timeFrameToDate(lastMonthTimeFrame),
      });

      // get the month before the last month
      const beforeLastMonthTimeFrame = sortMonthlyOptions[sortMonthlyOptions.length - 2];
      setMonthFrom({
        timeFrame: beforeLastMonthTimeFrame,
        date: timeFrameToDate(beforeLastMonthTimeFrame),
      });
    };

    if (!monthFrom && !monthTo) {
      if (sortMonthlyOptions.length > 0 && isCustomMonthly(currentValue)) {
        setCurrentlySelectedValueOnMonthPickers();
      } else {
        setLastTwoMonthsOnMonthPickers();
      }
    }
  }, [currentValue, sortMonthlyOptions, monthFrom, monthTo, sequenceToTimeFrame]);

  const handleApply = () => {
    const customTimeFramePeriod = {
      ...monthFrom?.timeFrame,
      value: `Monthly:${monthFrom?.timeFrame.endSequence}-${monthTo?.timeFrame.endSequence}:Custom`,
    };
    if (monthFrom && monthTo) {
      onSelect(customTimeFramePeriod, false);
    }
  };

  const lowestPossibleMonth = () => {
    const firstElement = sortMonthlyOptions[0];
    if (!firstElement) {
      return null;
    }
    return timeFrameToDate(firstElement);
  };

  const highestPossibleMonth = () => {
    const lastElement = sortMonthlyOptions[sortMonthlyOptions.length - 1];
    if (!lastElement) {
      return null;
    }
    return timeFrameToDate(lastElement);
  };

  return (
    <div
      data-testid="monthly-selector-container"
      className={`flex flex-column ${isMobile ? 'min-h-0 h-full justify-start' : 'h-auto justify-end'}`}
    >
      <div
        data-testid="monthly-selector"
        className={'p-4 pt-0'}
        style={{
          marginBottom: isMobile ? '0' : 'auto',
        }}
      >
        <MonthPicker
          label={'From'}
          dateFormat={MonthFormatType['MMM yyyy']}
          defaultDate={monthFrom?.date}
          minDate={lowestPossibleMonth()}
          maxDate={highestPossibleMonth()}
          onChange={(val: Date) => {
            setMonthFrom(dateToTimeFrame(val, monthlyOptions));
          }}
        />
        <MonthPicker
          label={'To'}
          dateFormat={MonthFormatType['MMM yyyy']}
          defaultDate={monthTo?.date}
          minDate={lowestPossibleMonth()}
          maxDate={highestPossibleMonth()}
          onChange={(val: Date) => {
            setMonthTo(dateToTimeFrame(val, monthlyOptions));
          }}
        />
        {isWrongDateRange && <span className="text-ui-red">{WARNING_MESSAGE_ON_APPLY}</span>}
      </div>
      <hr />

      <ApplyPeriodButton
        notPublishedMarkets={notPublishedMarkets}
        onApply={handleApply}
        disabled={!monthFrom || !monthTo || isWrongDateRange}
      />
    </div>
  );
}
